// Canonical Sill Pro (CRM) marketing + pro-app pricing copy. Shared between
// the marketing `/pro/pricing` surface and the pro app's `/pricing` page so
// the plan comparison never drifts across the two surfaces. Mirrors the
// `pricing-copy.ts` idiom in this directory (the consumer Sill Plus copy) —
// see that file for the sibling pattern.
//
// PRO_FALLBACK_PRICE is the ONE sanctioned CRM price literal on the
// frontend. It exists only because the marketing site is unauthenticated and
// has no session-backed catalog fetch of its own — every other CRM billing
// surface (the pro app) renders the live backend catalog
// (`crmbilling.CatalogProjection` / `CRMBillingSummaryVM.catalog`) and falls
// back to its own `FALLBACK_CATALOG` (frontend/pro/src/lib/view-model.ts,
// which derives from this constant) only when the backend hasn't shipped a
// catalog. See backend/internal/services/crmbilling/catalog.go
// (CurrentCatalog) for the backend-owned source of truth these numbers
// mirror, and GET /api/public/crm-pricing for the runtime fetch the
// marketing page prefers over this literal.

// PRO_FREE_PLAN_FEATURES is genuinely-free POSITIVES only. The gated-away
// capabilities (people cap, campaigns/automations, client detail, bulk
// outreach, AI cap) render as PRO_FREE_PLAN_LIMITS — a separate muted list —
// so the free card is honest about what it does NOT include rather than
// dressing a cap up as a feature.
export const PRO_FREE_PLAN_FEATURES: string[] = [
  "Full marketplace tools — profile, offers, connections",
  "People roster with pipeline counts",
  "Your referral link + weekly digest",
];

/**
 * Free-plan LIMITS, rendered under PRO_FREE_PLAN_FEATURES with a muted
 * dash/minus treatment (never destructive red — these are caps, not errors).
 */
export const PRO_FREE_PLAN_LIMITS: string[] = [
  "25 people max",
  "No campaigns or automations",
  "No client detail pages",
  "No bulk outreach",
  "AI assistant capped at 3 drafts a day",
];

export const PRO_PLAN_FEATURES: string[] = [
  "Unlimited people",
  "AI assistant — 50 a day",
  "Campaigns, automations & templates",
  "Bulk outreach",
  "Full client detail & activity",
  "Everything in Free",
];

export const PRO_COMING_SOON: string[] = [];

export const PRO_FALLBACK_PRICE = {
  monthlyCents: 4900,
  annualCents: 49_000,
} as const;

/**
 * Number of months of the monthly rate the annual price saves, e.g. 2 for
 * $49/mo vs $490/yr (12 * 49 = 588; (588 - 490) / 49 = 2). Pure function of
 * the two catalog prices so it never drifts from whatever `monthlyCents`/
 * `annualCents` the caller passes (live catalog fetch or the fallback
 * above). Never negative; 0 when annual offers no discount or monthlyCents
 * is 0 (avoids a divide-by-zero).
 */
export function proAnnualSavingsMonths(
  monthlyCents: number,
  annualCents: number,
): number {
  if (monthlyCents <= 0) return 0;
  const monthlyEquivalentCents = monthlyCents * 12;
  return Math.max(
    0,
    Math.round((monthlyEquivalentCents - annualCents) / monthlyCents),
  );
}

/**
 * Short savings-badge label for the annual toggle pill, e.g. "2 months
 * free". Returns null when annual offers no discount (the badge should not
 * render at all rather than show "0 months free").
 */
export function proAnnualSavingsLabel(
  monthlyCents: number,
  annualCents: number,
): string | null {
  const months = proAnnualSavingsMonths(monthlyCents, annualCents);
  if (months <= 0) return null;
  return `${months} month${months === 1 ? "" : "s"} free`;
}

/**
 * Renders the annual-plan subordinate line from raw cents, e.g.
 * "or $490/yr — 2 months free." Operator 2026-08-07: annual is purchasable
 * at activation (not portal-only), so this reads as a plain alternative
 * price, not a post-activation upsell. Pure function of the two catalog
 * prices so it never drifts from whatever `monthlyCents`/`annualCents` the
 * caller passes (live catalog fetch or the fallback above).
 */
export function proAnnualFootnote(
  monthlyCents: number,
  annualCents: number,
): string {
  const annualDollars = Math.round(annualCents / 100);
  const label = proAnnualSavingsLabel(monthlyCents, annualCents);
  return label
    ? `or $${annualDollars}/yr — ${label}.`
    : `or $${annualDollars}/yr.`;
}

export type ProBillingPeriod = "monthly" | "annual";

/**
 * The subordinate "or the other interval" line under a period toggle's
 * selected price — `proAnnualFootnote` when monthly is selected (advertises
 * annual + savings), or the plain monthly alternative when annual is
 * selected. Both marketing's ProPricingPanel and the pro app's PricingPage
 * render this under the price so the two surfaces never phrase it
 * differently.
 */
export function proOtherPeriodFootnote(
  period: ProBillingPeriod,
  monthlyCents: number,
  annualCents: number,
): string {
  if (period === "monthly") return proAnnualFootnote(monthlyCents, annualCents);
  const monthlyDollars = Math.round(monthlyCents / 100);
  return `or $${monthlyDollars}/mo, billed monthly.`;
}
